import './AddDocumentButton.scss';

import React, { FC, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { useCmsPostSearch } from 'customHooks/useCmsPostSearch';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import DataWrapper from 'interfaces/DataWrapper';
import { CmsPage } from 'models/CmsPage';
import moment from 'moment';
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Dropdown,
  DropdownButton,
  Form,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { Entities } from '../../../models/Entities';
import GenerateTopics from '../GenerateTopics/GenerateTopics';


// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface AddDocumentButtonProps { }

const AddDocumentButton: FC<AddDocumentButtonProps> = () => {
  const navigate = useNavigate();
  const [importModalShow, setImportModalShow] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const { configRepo, selectedProjectId, notify } = useContext(AppContext);
  const onTokenExpired = useTokenExpiredHandler();

  const [generateModalShow, setGenerateModalShow] = React.useState(false);

  const [isSearching, setIsSearching] = useState(false);
  const [hasNoResults, setHasNoResults] = useState(false);
  const onSearchComplete = (results: CmsPage[] | DataWrapper<CmsPage>) => {
    setIsSearching(false);
    const numberResults = (results as CmsPage[]).length;
    if (numberResults === 0) {
      setHasNoResults(true);
    } else {
      setHasNoResults(false);
    }
  };

  const { cmsPages, searchPosts, importPost } = useCmsPostSearch(
    configRepo!.PUBLISH_API_BASE_URI,
    onTokenExpired,
    onSearchComplete,
  );

  const onDropdownSelection = async (eventKey: string | null, e: SyntheticEvent<unknown>) => {
    e.preventDefault();
    if (eventKey === 'new') {
      navigate(`/${Entities.Documents}/edit/new`);
    } else if (eventKey === 'import') {
      setImportModalShow(true);
    } else {
      setGenerateModalShow(true);
    }
  };

  const onSearch = (_: SyntheticEvent | null) => {
    if (!searchText.trim()) {
      return;
    }
    setIsSearching(true);
    searchPosts(searchText, selectedProjectId!);
  };

  const onImport = async (id: number) => {
    const importedPost = await importPost(selectedProjectId!, id);
    if (importedPost.isSuccessful) {
      const docId = importedPost.entityId;
      navigate(`/${Entities.Documents}/edit/${docId}?cmsCategoryId=${importedPost.categoryId}`);
    } else {
      notify(`Failed to import CMS entry with id: ${id}`, NotificationType.Error);
    }
  };

  useEffect(() => {
    if (cmsPages.isError) {
      notify(
        `Failed to search or import from CMS due to server error.  Please try again or reconfigure your CMS connection.`,
        NotificationType.Error,
      );
    }
  }, [cmsPages]);

  const handleKeypress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch(null);
    }
  };

  return (
    <div>
      <ButtonGroup>
        <DropdownButton
          as={ButtonGroup}
          title="Add New..."
          id="bg-nested-dropdown"
          onSelect={onDropdownSelection}
          className="btngrp-create-import"
        >
          <Dropdown.Item eventKey="new">
            <i className='bi bi-pencil pr-1' />
            New Article
          </Dropdown.Item>
          <Dropdown.Item eventKey="import">
            <i className='bi bi-box-arrow-in-down pr-1' />
            Import from CMS
          </Dropdown.Item>
          <Dropdown.Item eventKey="generate" id="generateTopics">
            <i className="bi bi-magic pr-1" />
            Generate Topics
          </Dropdown.Item>
        </DropdownButton>
      </ButtonGroup>

      {/* Import Modal */}
      <Modal
        show={importModalShow}
        onHide={() => setImportModalShow(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">Import Article from CMS</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form as={Container}>
            <Form.Group as={Row} className="mb-3" controlId="searchInput">
              <Form.Label className="visually-hidden">Search Terms</Form.Label>
              <Col md={11} className="gx-0">
                <Form.Control
                  type="text"
                  placeholder="Search terms or article id..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  onKeyDown={handleKeypress}
                />
              </Col>
              <Col md={1} className="gx-0">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={onSearch}
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <span id="loadingStatus">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Loading...</span>
                    </span>
                  ) : (
                    <i className="bi bi-search" />
                  )}
                </button>
              </Col>
            </Form.Group>
          </Form>
          {(hasNoResults) ?
            'No search results found for the specified search term. Try a different search term or try searching by post ID.'
            :
            <Table>
              <thead>
                <tr>
                  <th aria-label="select action header">&nbsp;</th>
                  <th>Id</th>
                  <th>Title</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(cmsPages.responseData as CmsPage[]).map((p) => (
                  <tr key={p.pageID}>
                    <td>
                      <Button
                        variant="primary"
                        onClick={(e) => {
                          e.preventDefault();
                          onImport(p.pageID!);
                        }}
                      >
                        Select
                      </Button>
                    </td>
                    <td>{p.pageID}</td>
                    <td>{p.pageTitle}</td>
                    <td>{moment(p.publishedDate).format('MM/DD/YYYY')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          }
        </Modal.Body>
        <Modal.Footer>
          <a href="https://blabberjax.atlassian.net/wiki/spaces/BKP/pages/428146708/Article+Import+Wizard+Help" target='_blank' rel="noreferrer">
            Trouble importing? See help
          </a>
          <Button onClick={() => setImportModalShow(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <GenerateTopics
        generateModalShow={generateModalShow}
        setGenerateModalShow={setGenerateModalShow}
      />
    </div>
  );
};

export default AddDocumentButton;
